import { nanoid } from "nanoid";
import { useAppStore } from "../../../../../../../../hooks/useStores";
import useScenesUtils from "../../../../../../../CustomUtils/ScenesUtils";
import { getDefaultTemplate } from "../../../../../../../constants/design-editor";
import useDesignEditorContext from "../../../../../../../hooks/useDesignEditorContext";
import { useEditor } from "../../../../../../../hooks/useEditor";
import { useFrame } from "../../../../../../../hooks/useFrame";
import { IScene } from "../../../../../../../types";

const useSceneOperations = () => {
	const {
		setScenes,
		setContextMenuTimelineRequest,
		contextMenuTimelineRequest,
		setCurrentScene,
		setCurrentDesign,
		currentScene,
	} = useDesignEditorContext();
	const frame = useFrame();
	const editor = useEditor();
	const { updateScenes } = useScenesUtils();
	const { campaignStore } = useAppStore();

	const deleteScene = async (id?: string) => {
		const scenes = await updateScenes();
		const updatedScenes = id
			? scenes.filter((scene) => scene.id !== id)
			: scenes.filter((scene) => scene.id !== contextMenuTimelineRequest.id);
		const sceneToDelete: IScene | undefined = id
			? scenes.find((scene: IScene) => scene.id === id)
			: scenes.find((scene: IScene) => scene.id === contextMenuTimelineRequest.id);

		campaignStore.sceneToDelete = sceneToDelete;

		setContextMenuTimelineRequest({ ...contextMenuTimelineRequest, visible: false });

		if (updatedScenes.length > 0) {
			if (currentScene?.id !== id && contextMenuTimelineRequest.id !== currentScene?.id)
				editor?.history.save(updatedScenes);
			setScenes(updatedScenes);
		} else {
			const defaultTemplate = getDefaultTemplate({
				width: frame.width,
				height: frame.height,
			});

			await editor?.scene.importFromJSON(defaultTemplate);
			setCurrentDesign({
				id: nanoid(),
				frame: defaultTemplate.frame,
				metadata: {},
				name: "Untitled Design",
				preview: "",
				scenes: [],
			});

			const initialDesign = editor?.scene.exportToJSON() as IScene;
			const preview = await editor?.renderer.render(initialDesign);
			setCurrentScene({ ...initialDesign, preview, duration: 5000 });
			setScenes([{ ...initialDesign, preview, duration: 5000 }]);
		}
		campaignStore.originalScenesBeforeResizing = null;
	};

	const duplicateScene = async (id?: string) => {
		const scenes = await updateScenes();
		const currentScene: IScene | undefined = id
			? scenes.find((scene: IScene) => scene.id === id)
			: scenes.find((scene: IScene) => scene.id === contextMenuTimelineRequest.id);
		if (currentScene) {
			const updatedScenes: IScene[] = [...scenes, { ...currentScene, id: nanoid(), isMainScene: false }];
			setScenes(updatedScenes);
			editor?.history.save(updatedScenes);
			setContextMenuTimelineRequest({ ...contextMenuTimelineRequest, visible: false });
		}
		campaignStore.originalScenesBeforeResizing = null;
	};

	const downloadScene = async () => {
		const scenes = await updateScenes();
		const currentScene: IScene | undefined = scenes.find(
			(scene: IScene) => scene.id === contextMenuTimelineRequest.id,
		);
		if (currentScene) {
			const firstLayer = currentScene.layers[0];
			const width = firstLayer.width;
			const height = firstLayer.height;
			if (width && height)
				currentScene.frame = {
					width: width,
					height: height,
				};
			const renderedImage: string | undefined = await editor?.renderer.render(currentScene);
			if (renderedImage) {
				const link = document.createElement("a");
				link.href = renderedImage;
				link.download = `${currentScene.name}.png`;
				document.body.appendChild(link);
				link.click();
				document.body.removeChild(link);
			}
		}
	};

	return { deleteScene, duplicateScene, downloadScene };
};

export default useSceneOperations;
